import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Ecu } from '../../../services/models/assets';
import { LockUsageDataPoint } from '../../../services/SubscriptionService';

interface LockUsageChartProps {
  ecus: Ecu[];
  lockUsageData: LockUsageDataPoint[];
  isLoading?: boolean;
}

const LockUsageChart: React.FC<LockUsageChartProps> = ({ ecus, lockUsageData, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex-[2] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-neutral typo-h3">Utilisation des locks</h3>
        </div>
        <div className="h-[200px] flex items-center justify-center">
          <span className="text-neutral-40 typo-small">Chargement...</span>
        </div>
      </div>
    );
  }
  const sortedEcus = [...ecus].sort((a, b) => a.address - b.address);
  const allLocks = sortedEcus.flatMap((ecu) =>
    (ecu.locks || []).map((lock) => ({
      ...lock,
      ecuLabel: ecu.isPrincipal ? 'P' : `S${ecu.address}`,
    }))
  );

  const data = allLocks
    .map((lock) => {
      const usage = lockUsageData.find((d) => d.lockId === lock.id);
      const value = usage ? Number(usage.count) : 0;
      const number = lock.position + 1;

      return {
        number,
        name: `Lock ${number}`,
        value,
      };
    })
    .sort((a, b) => a.number - b.number);

  const totalOpenings = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="flex-[2] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-neutral typo-h3">Utilisation des locks</h3>
        <span className="typo-small">
          <span className="text-neutral">{totalOpenings}</span>
          <span className="text-neutral-60"> ouvertures</span>
        </span>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid stroke="rgba(26,26,26,0.05)" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickCount={5}
              interval={0}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
              tickLine={false}
              axisLine={false}
              interval={0}
              spacing={4}
              width={64}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1A1A1A',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '13px',
                color: 'white',
              }}
              cursor={{ fill: 'rgba(26,26,26,0.03)' }}
              formatter={(value: any, name: any) =>
                name === 'value' ? [`${value} ouvertures`] : null
              }
            />
            <Bar
              dataKey="value"
              stackId="lock"
              barSize={32}
              fill="#FF8A4C"
              radius={4}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LockUsageChart;
